<script lang="ts" setup>
import { ArrowLongLeftIcon, ArrowLongRightIcon } from '@heroicons/vue/20/solid'
import { PaginationMeta } from '@/Support/Queries/Interfaces/PaginationMeta'
import { t } from '@/Support/lang'
import { computed } from 'vue'

interface Props {
  data: PaginationMeta | null | undefined
  slim?: boolean
  maxPages?: number
  borderTop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  slim: false,
  maxPages: 7,
  borderTop: true,
})

const emit = defineEmits<{
  (e: 'change', value: number): void
}>()
/**
 * Pages
 */
const maxRangePage = props.maxPages - 2
const rangePages = Math.floor(props.maxPages / 2)
const pages = computed(() =>
  props.data ? Math.ceil(props.data.total / props.data.per_page) : 1,
)
const currentPage = computed(() => props.data?.current_page || 1)
const maxVisiblePage = computed(() => pages.value - maxRangePage)
const maxFirstPages = computed(() =>
  Math.min(currentPage.value, maxVisiblePage.value),
)
const firstPages = computed(() =>
  Array.from({ length: rangePages }, (x, i) => i + maxFirstPages.value),
)
const lastPages = computed(() =>
  Array.from(
    { length: rangePages },
    (x, i) => i + (pages.value - rangePages) + 1,
  ),
)
const changePage = (value: number) => {
  let newPage = currentPage.value + value
  if (newPage < 1) {
    newPage = 1
  }
  if (newPage > pages.value) {
    newPage = pages.value
  }
  emit('change', newPage)
}

const numberStyle = (currentPage: boolean) => {
  return {
    'border-primary-500 text-primary-700 hover:text-primary-600 hover:border-primary-600 dark:border-primary-300 dark:text-primary-300 dark:hover:text-primary-400 dark:hover:border-primary-400':
      currentPage,
    'border-transparent text-neutral-400 hover:text-neutral-300 hover:border-neutral-300 dark:text-neutral-500 dark:hover:text-neutral-600 dark:hover:border-neutral-500':
      !currentPage,
    'inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium cursor-pointer':
      true,
  }
}
</script>
<template>
  <nav
    v-if="pages > 1"
    class="flex items-center justify-between dark:border-neutral-700 px-4"
    :class="{
      'border-t border-neutral-200 dark:border-neutral-700': borderTop,
    }"
  >
    <div class="-mt-px flex w-0 flex-1">
      <a
        class="cursor-pointer flex items-center border-t-2 border-transparent pt-4 pr-1 mr-2 hover:border-neutral-300"
        @click="changePage(-pages)"
      >
        <ArrowLongLeftIcon
          aria-hidden="true"
          class="h-5 w-5 text-neutral-400 dark:text-neutral-300 border-l border-neutral-400"
        />
      </a>
      <a
        class="cursor-pointer inline-flex items-center border-t-2 border-transparent pt-4 pr-1 text-sm font-medium text-neutral-500 dark:text-neutral-300 hover:border-neutral-300 hover:text-neutral-700 dark:hover:text-neutral-100"
        @click="changePage(-1)"
      >
        <ArrowLongLeftIcon
          aria-hidden="true"
          class="mr-3 h-5 w-5 text-neutral-400 dark:text-neutral-300"
        />
        <span v-if="!slim">{{ t('Previous') }}</span>
      </a>
    </div>
    <div class="hidden md:-mt-px md:flex">
      <div v-if="pages < maxPages">
        <a
          v-for="index in pages"
          :key="index"
          :class="numberStyle(index === data.current_page)"
          @click="$emit('change', index)"
        >
          {{ index }}
        </a>
      </div>
      <div v-else>
        <a
          v-for="index in firstPages"
          :key="index"
          :class="numberStyle(index === data.current_page)"
          @click="$emit('change', index)"
        >
          {{ index }}
        </a>

        <span
          v-show="maxVisiblePage > currentPage"
          class="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-neutral-500"
        >
          ...
        </span>

        <a
          v-for="index in lastPages"
          :key="index"
          :class="numberStyle(index === data.current_page)"
          @click="$emit('change', index)"
        >
          {{ index }}
        </a>
      </div>
    </div>
    <div class="-mt-px flex w-0 flex-1 justify-end">
      <a
        class="cursor-pointer inline-flex items-center border-t-2 border-transparent pt-4 pl-1 text-sm font-medium text-neutral-500 dark:text-neutral-300 hover:border-neutral-300 hover:text-neutral-700 dark:hover:text-neutral-100"
        @click="changePage(1)"
      >
        <span v-if="!slim">{{ t('Next') }}</span>
        <ArrowLongRightIcon
          aria-hidden="true"
          class="ml-3 h-5 w-5 text-neutral-400 dark:text-neutral-300"
        />
      </a>
      <a
        class="cursor-pointer flex border-t-2 border-transparent pt-4 pr-1 ml-2 hover:border-neutral-300"
        @click="changePage(pages)"
      >
        <ArrowLongRightIcon
          aria-hidden="true"
          class="h-5 w-5 text-neutral-400 dark:text-neutral-300 border-neutral-400 border-r"
        />
      </a>
    </div>
  </nav>
</template>
